function durationInMilliseconds(value, fallback) {
  const normalizedValue = value.trim();
  const duration = Number.parseFloat(normalizedValue);
  if (!Number.isFinite(duration)) return fallback;
  return normalizedValue.endsWith('s') && !normalizedValue.endsWith('ms') ? duration * 1000 : duration;
}

const SENTENCE_END = /[.!?…][)\]"'”’]?$/;

const clampInstances = new WeakMap();
let clampResizeObserver;

/**
 * One observer for every clamped body on the page: each entry routes back to its
 * own instance, whose measurement then coalesces through the frame scheduler.
 */
function observeClampSize(instance) {
  if (!(instance.content instanceof HTMLElement)) return;

  if (!clampResizeObserver) {
    clampResizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => clampInstances.get(entry.target)?.measureClampOverflow());
    });
  }

  clampInstances.set(instance.content, instance);
  clampResizeObserver.observe(instance.content);
}

function unobserveClampSize(instance) {
  if (!clampResizeObserver || !(instance.content instanceof HTMLElement)) return;
  clampResizeObserver.unobserve(instance.content);
  clampInstances.delete(instance.content);
}

if (!customElements.get('dieux-read-more')) {
  class DieuxReadMore extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.heightMeasureKey = Symbol('text-read-more-height');
      this.overflowMeasureKey = Symbol('text-read-more-overflow');
      this.transitionVersion = 0;
      this.targetExpanded = false;
    }

    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.toggle = this.querySelector('[data-text-read-more-toggle]');
      this.content = this.querySelector('[data-text-read-more-content]');
      this.line = this.querySelector('[data-text-read-more-line]');

      if (!this.hasParts()) return;

      this.toggle.addEventListener('click', this.handleClick, {
        signal: this.abortController.signal,
      });
      this.addEventListener('keydown', this.handleKeydown, {
        signal: this.abortController.signal,
      });
      this.targetExpanded = this.toggle.getAttribute('aria-expanded') === 'true';
      this.commitExpanded(this.targetExpanded);
      this.describeByHeading();

      observeClampSize(this);
      this.measureClampOverflow();
    }

    disconnectedCallback() {
      this.transitionVersion += 1;
      unobserveClampSize(this);
      window.DieuxFrame?.cancelMeasure(this.heightMeasureKey);
      window.DieuxFrame?.cancelMeasure(this.overflowMeasureKey);
      this.abortController?.abort();
      this.heightAnimation?.cancel();
      this.heightAnimation = undefined;
      if (this.hasParts()) {
        this.commitExpanded(this.targetExpanded);
      }
      this.abortController = undefined;
      this.toggle = undefined;
      this.content = undefined;
      this.line = undefined;
    }

    hasParts() {
      return this.toggle instanceof HTMLButtonElement && this.content instanceof HTMLElement;
    }

    /** Describes a toggle with no rendered context by the heading that leads its list item. */
    describeByHeading() {
      if (this.toggle.querySelector('.visually-hidden') || !this.content.id) return;

      const item = this.closest('li, [role="listitem"], [data-glossary-item]');
      if (!item) return;

      const heading = Array.from(item.querySelectorAll('h1, h2, h3, h4, h5, h6'))
        .filter((node) => !this.contains(node) && node.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING)
        .pop();
      if (!heading) return;

      if (!heading.id) heading.id = `${this.content.id}-Heading`;
      const ids = (this.toggle.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (!ids.includes(heading.id)) this.toggle.setAttribute('aria-describedby', [...ids, heading.id].join(' '));
    }

    applyExpandedState(expanded) {
      this.toggle.setAttribute('aria-expanded', String(expanded));
      this.toggleAttribute('data-read-more-expanded', expanded);
    }

    commitExpanded(expanded) {
      if (!this.hasParts()) return;

      const toggleHadFocus = document.activeElement === this.toggle;

      this.targetExpanded = expanded;
      this.removeAttribute('data-text-read-more-resizing');
      this.style.removeProperty('block-size');
      this.applyExpandedState(expanded);

      if (toggleHadFocus) this.toggle.focus({ preventScroll: true });
    }

    /**
     * Collapses at once, discarding any pending or running height transition.
     *
     * @returns {boolean} True when the body was expanded or on its way there.
     */
    collapse() {
      if (!this.hasParts()) return false;
      if (!this.targetExpanded && !this.heightAnimation) return false;

      this.transitionVersion += 1;
      window.DieuxFrame?.cancelMeasure(this.heightMeasureKey);
      this.heightAnimation?.cancel();
      this.heightAnimation = undefined;
      this.commitExpanded(false);
      return true;
    }

    /**
     * A clamped body can only report an overflow while it is capped, so an
     * expanded one keeps the toggle it demonstrably earned.
     */
    measureClampOverflow() {
      if (!this.hasParts()) return;

      if (this.targetExpanded || this.hasAttribute('data-read-more-cut')) {
        this.toggle.hidden = false;
        return;
      }

      const frame = window.DieuxFrame;
      if (!frame) {
        const overflows = this.content.scrollHeight - this.content.clientHeight > 1;
        this.applyClampOverflow(overflows);
        this.applyClampedCut(overflows ? this.measureClampedCut() : null);
        return;
      }

      frame.cancelMeasure(this.overflowMeasureKey);
      frame.measure(this.overflowMeasureKey, () => {
        if (!this.isConnected || !this.hasParts() || this.targetExpanded) return;
        const overflows = this.content.scrollHeight - this.content.clientHeight > 1;
        const cut = overflows ? this.measureClampedCut() : null;

        frame.write(() => {
          if (!this.isConnected || !this.hasParts()) return;
          const revealed = this.applyClampOverflow(overflows);
          this.applyClampedCut(cut);
          // The cut is the toggle's own edge, which has no box until it is revealed.
          if (revealed && overflows) this.measureClampOverflow();
        });
      });
    }

    /**
     * How far along its last visible line the clamped text runs, so an inline toggle can sit at the end of the sentence instead of at the block end.
     *
     * @returns {?{end: number, sentence: boolean}}
     */
    measureClampedCut() {
      if (!this.line) return null;

      const bodyRect = this.content.getBoundingClientRect();
      if (!bodyRect.height) return null;

      const rightToLeft = window.getComputedStyle(this.content).direction === 'rtl';
      const walker = document.createTreeWalker(this.content, NodeFilter.SHOW_TEXT);
      const range = document.createRange();
      let lastLineBottom = -Infinity;
      let textEnd = null;
      let endNode = null;

      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (!node.data.trim()) continue;
        range.selectNodeContents(node);

        for (const rect of range.getClientRects()) {
          // Lines the clamp cut off are still laid out below the body's own box.
          if (!rect.width || rect.bottom > bodyRect.bottom + 1) continue;
          const inset = rightToLeft ? bodyRect.right - rect.left : rect.right - bodyRect.left;

          if (rect.bottom > lastLineBottom + 1) {
            lastLineBottom = rect.bottom;
            textEnd = inset;
            endNode = node;
          } else if (rect.bottom > lastLineBottom - 1 && inset > textEnd) {
            textEnd = inset;
            endNode = node;
          }
        }
      }

      if (textEnd === null) return null;

      return {
        end: Math.round(textEnd),
        sentence: this.cutFollowsSentence(endNode, bodyRect, rightToLeft),
      };
    }

    /**
     * Whether the last character the toggle leaves visible already closes a
     * sentence, which the ellipsis would otherwise follow with a second mark.
     * The toggle is opaque, so its own edge is the cut rather than the end of
     * the text the clamp kept.
     *
     * @param {Text} node
     * @param {DOMRect} bodyRect
     * @param {boolean} rightToLeft
     * @returns {boolean}
     */
    cutFollowsSentence(node, bodyRect, rightToLeft) {
      const toggleRect = this.toggle.hidden ? null : this.toggle.getBoundingClientRect();
      if (!node || !toggleRect || !toggleRect.width) {
        return this.hasAttribute('data-read-more-sentence-cut');
      }

      const cut = rightToLeft ? toggleRect.right : toggleRect.left;
      const range = document.createRange();
      let visibleEnd = 0;
      let low = 0;
      let high = node.data.length;

      // The end of a range only ever moves forward, so the cut can be bisected.
      while (low < high) {
        const middle = Math.ceil((low + high) / 2);
        range.setStart(node, 0);
        range.setEnd(node, middle);
        const rects = range.getClientRects();
        const last = rects[rects.length - 1];
        const before =
          last &&
          last.bottom <= bodyRect.bottom + 1 &&
          (rightToLeft ? last.left >= cut - 0.5 : last.right <= cut + 0.5);

        if (before) {
          visibleEnd = middle;
          low = middle;
        } else {
          high = middle - 1;
        }
      }

      return SENTENCE_END.test(node.data.slice(0, visibleEnd).trimEnd());
    }

    applyClampOverflow(overflows) {
      if (this.toggle.hidden === !overflows) return false;
      this.toggle.hidden = !overflows;
      return true;
    }

    applyClampedCut(cut) {
      if (!this.line) return;

      if (cut === null) {
        this.style.removeProperty('--g-read-more-toggle-start');
        this.removeAttribute('data-read-more-sentence-cut');
        return;
      }

      this.style.setProperty('--g-read-more-toggle-start', `${cut.end}px`);
      this.toggleAttribute('data-read-more-sentence-cut', cut.sentence);
    }

    requestExpanded(expanded) {
      if (!this.hasParts()) return;

      this.targetExpanded = expanded;
      const frame = window.DieuxFrame;
      if (!frame || typeof this.animate !== 'function') {
        this.heightAnimation?.cancel();
        this.heightAnimation = undefined;
        this.commitExpanded(expanded);
        return;
      }

      const transitionVersion = ++this.transitionVersion;
      frame.cancelMeasure(this.heightMeasureKey);
      frame.measure(this.heightMeasureKey, () => {
        if (transitionVersion !== this.transitionVersion || !this.isConnected) return;

        const startHeight = this.getBoundingClientRect().height;
        const styles = window.getComputedStyle(this);
        const duration = durationInMilliseconds(
          styles.getPropertyValue('--motion-duration-standard'),
          200
        );
        const easing = styles.getPropertyValue('--motion-easing').trim() || 'ease-in-out';
        const toggleHadFocus = document.activeElement === this.toggle;

        frame.write(() => {
          if (transitionVersion !== this.transitionVersion || !this.isConnected) return;
          this.beginResize({
            expanded,
            startHeight,
            duration,
            easing,
            toggleHadFocus,
            transitionVersion,
          });
        });
      });
    }

    beginResize({ expanded, startHeight, duration, easing, toggleHadFocus, transitionVersion }) {
      if (!this.hasParts()) return;

      this.heightAnimation?.cancel();
      this.heightAnimation = undefined;
      this.removeAttribute('data-text-read-more-resizing');
      this.style.removeProperty('block-size');
      this.applyExpandedState(expanded);

      const targetHeight = this.getBoundingClientRect().height;

      this.setAttribute('data-text-read-more-resizing', expanded ? 'expanding' : 'contracting');

      if (toggleHadFocus) this.toggle.focus({ preventScroll: true });

      if (
        duration <= 0 ||
        !Number.isFinite(startHeight) ||
        !Number.isFinite(targetHeight) ||
        Math.abs(startHeight - targetHeight) < 1
      ) {
        this.commitExpanded(expanded);
        return;
      }

      this.heightAnimation = this.animate(
        [{ blockSize: `${startHeight}px` }, { blockSize: `${targetHeight}px` }],
        { duration, easing, fill: 'both' }
      );
      const animation = this.heightAnimation;

      animation.finished
        .then(() => {
          if (transitionVersion !== this.transitionVersion || animation !== this.heightAnimation) {
            return;
          }

          this.heightAnimation = undefined;
          this.commitExpanded(expanded);
          animation.cancel();
        })
        .catch(() => {});
    }

    handleClick() {
      this.requestExpanded(!this.targetExpanded);
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (!this.targetExpanded) return;

      event.preventDefault();
      this.requestExpanded(false);
    }
  }

  customElements.define('dieux-read-more', DieuxReadMore);
}
